import { useId } from 'react';

import { cn } from '@/lib/cn';
import type { CoverColour } from '@/lib/api/types';

export interface CoverColourPickerProps {
  colours: CoverColour[];
  /** Slug of the selected colour. */
  value: string | null;
  onChange: (slug: string) => void;
  /** The binding this colours, for the legend — "Project binding" means more
   * than "binding" when there are several in the catalogue. */
  bindingLabel?: string;
}

/**
 * Which colour the cover is printed on.
 *
 * Swatches rather than a dropdown, because the choice is about how it will
 * LOOK. Three things keep it honest:
 *
 * **The name is always shown, never only the colour.** A swatch alone fails
 * WCAG 1.4.1 and fails anyone with a colour vision deficiency, and "navy" and
 * "black" are two of the hardest pairs to tell apart on a phone screen in
 * sunlight.
 *
 * **The hex is approximate and the copy says so.** It is a screen rendering of
 * a physical card; promising an exact match is a complaint waiting to happen.
 *
 * **Selection is a real radio group**, like the other pickers in this flow, so
 * arrow keys work and the control cannot fall out of sync with the a11y tree.
 */
export function CoverColourPicker({
  colours,
  value,
  onChange,
  bindingLabel,
}: CoverColourPickerProps) {
  const id = useId();
  const hintId = `${id}-hint`;

  if (colours.length === 0) return null;

  return (
    <fieldset className="flex flex-col gap-2" aria-describedby={hintId}>
      <legend className="mb-1 text-sm font-medium text-ink">
        {bindingLabel ? `${bindingLabel} cover colour` : 'Cover colour'}
      </legend>
      <p id={hintId} className="-mt-1 text-sm text-ink-subtle">
        Printed on card. The colours here are close, but a screen never matches card exactly.
      </p>

      <div className="flex flex-wrap gap-2">
        {colours.map((colour) => {
          const checked = value === colour.slug;
          return (
            <label
              key={colour.slug}
              className="cursor-pointer"
              // Title, not a tooltip library: the label is already visible, so
              // this is only a convenience for a truncated one.
              title={colour.label}
            >
              <input
                type="radio"
                name={`${id}-cover-colour`}
                value={colour.slug}
                checked={checked}
                onChange={() => onChange(colour.slug)}
                className="peer sr-only"
              />
              <span
                className={cn(
                  'flex min-h-touch items-center gap-2 rounded-card border px-3 py-2',
                  'border-line-strong bg-surface text-sm text-ink transition-colors',
                  'peer-checked:border-brand-ink peer-checked:bg-brand-soft',
                  'peer-checked:ring-1 peer-checked:ring-brand-ink',
                  'peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2',
                )}
              >
                <span
                  aria-hidden="true"
                  // Bordered so a white swatch is still a swatch on a white card.
                  className="h-5 w-5 shrink-0 rounded-full border border-line-strong"
                  style={{ backgroundColor: colour.hex }}
                />
                {/* Never the swatch alone: colour must not be the only signal. */}
                {colour.label}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
